import { styled, Card, Typography, Box } from "@material-ui/core";
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { formatNumber } from "components-library";
import { useMemo } from "react";

const StyledStatCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  border: `3px solid ${theme.palette.primary.main}`,
  flex: 1,
}));

const StatRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(1, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const ChangeIndicator = ({ value, originalValue, isRate = false }) => {
  const formattedValue = isRate ? formatNumber(value, 1) : formatNumber(value);
  const formattedOriginalValue = isRate ? formatNumber(originalValue, 1) : formatNumber(originalValue);
  const color = value > 0 ? 'green' : value < 0 ? 'red' : 'inherit';
  const Icon = value > 0 ? ArrowUpwardIcon : ArrowDownwardIcon;
  
  return (
    <Typography 
      variant="body2" 
      component="span" 
      style={{ color, display: 'inline-flex', alignItems: 'center' }}
    >
      {formattedOriginalValue} {"->"} ({formattedValue}% {value !== 0 && <Icon style={{ fontSize: 16 }} />})
    </Typography>
  );
};

function StatCard({ 
  title,
  last7Days, 
  last7DaysYoy,
  last30Days,
  last30DaysYoy,
  monthToDate,
  monthToDateLastYear,
  monthLastYearTotal,
  isRate = false
}) {
  const calculateYoyChange = (current, previous) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const yoyChange7Days = useMemo(() => 
    calculateYoyChange(last7Days, last7DaysYoy), 
    [last7Days, last7DaysYoy]
  );

  const yoyChange30Days = useMemo(() => 
    calculateYoyChange(last30Days, last30DaysYoy), 
    [last30Days, last30DaysYoy]
  );

  const yoyChangeMonth = useMemo(() => 
    calculateYoyChange(monthToDate, monthToDateLastYear), 
    [monthToDate, monthToDateLastYear]
  );

  const formatValue = (value) => {
    if (isRate) {
      return `${formatNumber(value * 100, 1)}%`;
    }
    return formatNumber(value);
  };

  return (
    <StyledStatCard>
      <Typography color="primary" variant="h6" style={{ fontWeight: 600 }}>
        {title}
      </Typography>

      {/* Last 7 Days */}
      <StatRow>
        <Box>
          <Typography variant="body2" color="textSecondary">
            Last 7 Days
          </Typography>
          <Typography variant="h6" color="primary" style={{ fontWeight: 600 }}>
            {formatValue(last7Days)}
          </Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="body2" color="textSecondary">
            YoY
          </Typography>
          <ChangeIndicator originalValue={last7DaysYoy} value={yoyChange7Days} isRate={isRate} />
        </Box>
      </StatRow>

      {/* Last 30 Days */}
      <StatRow>
        <Box>
          <Typography variant="body2" color="textSecondary">
            Last 30 Days
          </Typography>
          <Typography variant="h6" color="primary" style={{ fontWeight: 600 }}>
            {formatValue(last30Days)}
          </Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="body2" color="textSecondary">
            YoY
          </Typography>
          <ChangeIndicator originalValue={last30DaysYoy} value={yoyChange30Days} isRate={isRate} />
        </Box>
      </StatRow>

      {/* Month to Date */}
      <StatRow>
        <Box>
          <Typography variant="body2" color="textSecondary">
            Month to Date
          </Typography>
          <Typography variant="h6" color="primary" style={{ fontWeight: 600 }}>
            {formatValue(monthToDate)}
          </Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="body2" color="textSecondary">
            YoY
          </Typography>
          <ChangeIndicator originalValue={monthToDateLastYear} value={yoyChangeMonth} isRate={isRate} />
        </Box>
      </StatRow>
      <StatRow>
        <Box>
          <Typography variant="body2" color="textSecondary">
            This Month Last Year
          </Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="body2" color="primary" style={{ fontWeight: 600 }} variant="h6">
            {formatValue(monthLastYearTotal)}
          </Typography>
        </Box>
          
      </StatRow>
    </StyledStatCard>
  );
}

export default StatCard;